// frontend/src/components/ProductCategoryList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { 
  Edit, Trash2, Plus, Building, User, Tag, 
  Filter, Search, ChevronDown, AlertCircle,
  Loader2, Eye, Globe
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./ProductCategoryList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const ProductCategoryList = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // For system admins
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  useEffect(() => {
    fetchCategories();
    
    // System admins can filter by company
    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [selectedCompany]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      
      let endpoint = `${url}/api/productcategory`;
      const params = {};
      
      // System admins can filter by company
      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }
      
      const response = await axios.get(endpoint, { params });
      
      if (response.data.success) {
        setCategories(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch categories");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${url}/api/productcategory/${id}`);
      
      if (response.data.success) {
        toast.success(`"${name}" deleted successfully`);
        fetchCategories();
      } else {
        toast.error(response.data.message || "Failed to delete category");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete category");
    }
  };

  const handleViewCategory = (category) => {
    // Show category details in a modal or alert
    alert(`Category Details:\n\nName: ${category.category_name}\nCompany: ${category.company_id?.company_name}\nCreated By: ${category.created_by?.Usercomp_sure_name || category.created_by?.name}\nCreated: ${new Date(category.created_at).toLocaleDateString()}\nStatus: ${category.status}`);
  };

  const handleEditCategory = (id) => {
    navigate(`/productcategory/edit/${id}`);
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.company_id?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.created_by?.Usercomp_sure_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.created_by?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading product categories...</p>
      </div>
    );
  }

  return (
    <div className="productcategory-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>Product Categories</h2>
          <p className="company-info">
            {isSystemAdmin ? (
              <>
                <Globe size={14} /> All Companies
              </>
            ) : (
              <>
                <Building size={14} /> {company?.company_name} ({company?.company_code})
              </>
            )}
          </p>
        </div>
        
        <div className="header-actions">
          {/* Filter Toggle for System Admins */}
          {isSystemAdmin && (
            <button 
              className="filter-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} />
              Filter
            </button>
          )}
        </div>
      </div>

      {/* Filters for System Admins */}
      {isSystemAdmin && showFilters && (
        <div className="filters-container">
          <div className="filter-group">
            <label>Filter by Company:</label>
            <div className="select-wrapper">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={loadingCompanies}
              >
                <option value="">All Companies</option>
                {companies.map((comp) => (
                  <option key={comp._id} value={comp._id}>
                    {comp.company_name} ({comp.company_code})
                  </option>
                ))}
              </select>
              <Building size={16} className="select-icon" />
            </div>
          </div>
          
          <button 
            className="clear-filters"
            onClick={() => {
              setSelectedCompany("");
              setShowFilters(false);
            }}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search categories by name, company, or creator..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="stats">
          <span>Showing: {filteredCategories.length} of {categories.length} categories</span>
          {selectedCompany && (
            <span className="filter-badge">
              <Building size={12} />
              {companies.find(c => c._id === selectedCompany)?.company_name || "Filtered"}
            </span>
          )}
        </div>
      </div>

      {/* Categories Table */}
      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Category Name</th>
              {isSystemAdmin && <th>Company</th>}
              <th>Created By</th>
              <th>Created Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.length === 0 ? (
              <tr>
                <td colSpan={isSystemAdmin ? 7 : 6} className="no-data">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Tag size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666' }}>
                      {searchTerm || selectedCompany
                        ? "No categories found matching your criteria"
                        : "No product categories found"
                      }
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredCategories.map((category, index) => (
                <tr key={category._id}>
                  <td>{index + 1}</td>
                  <td>
                    <div className="category-name">
                      <Tag size={14} className="mr-2" color="#f89a3a" />
                      <strong>{category.category_name}</strong>
                    </div>
                  </td>
                  
                  {isSystemAdmin && (
                    <td>
                      <div className="company-cell">
                        <Building size={12} className="mr-2" />
                        <div>
                          <div>{category.company_id?.company_name || "N/A"}</div>
                          <small>{category.company_id?.company_code || ""}</small>
                        </div>
                      </div>
                    </td>
                  )}
                  
                  <td>
                    <div className="user-cell">
                      <User size={12} className="mr-2" />
                      <div>
                        <div>{category.created_by?.Usercomp_sure_name || category.created_by?.name || "System"}</div>
                        <small>{category.created_by?.Usercomp_code || category.created_by?.email || ""}</small>
                      </div>
                    </div>
                  </td>
                  
                  <td>{new Date(category.created_at).toLocaleDateString()}</td>
                  
                  <td>
                    <span className={`status-badge ${category.status}`}>
                      {category.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductCategoryList;